import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { DecoratorNode } from 'lexical';

export type SerializedSectionDividerNode = Spread<
  { type: 'section-divider'; version: 1 },
  SerializedLexicalNode
>;

export class SectionDividerNode extends DecoratorNode<null> {
  static getType(): string {
    return 'section-divider';
  }

  static clone(node: SectionDividerNode): SectionDividerNode {
    return new SectionDividerNode(node.__key);
  }

  constructor(key?: NodeKey) {
    super(key);
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    div.setAttribute('data-section-divider', 'true');
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.gap = '12px';
    div.style.margin = '16px 0';
    div.style.userSelect = 'none';

    const hrLeft = document.createElement('hr');
    hrLeft.style.flex = '1';
    hrLeft.style.border = 'none';
    hrLeft.style.borderTop = '1px solid currentColor';
    hrLeft.style.opacity = '0.15';

    const label = document.createElement('span');
    label.setAttribute('data-section-label', '');
    label.textContent = 'Section Break';
    label.style.fontSize = '0.7rem';
    label.style.fontWeight = '500';
    label.style.letterSpacing = '0.02em';
    label.style.opacity = '0.35';
    label.style.whiteSpace = 'nowrap';

    const hrRight = document.createElement('hr');
    hrRight.style.flex = '1';
    hrRight.style.border = 'none';
    hrRight.style.borderTop = '1px solid currentColor';
    hrRight.style.opacity = '0.15';

    div.appendChild(hrLeft);
    div.appendChild(label);
    div.appendChild(hrRight);

    return div;
  }

  updateDOM(): false {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      hr: () => ({
        conversion: () => ({ node: $createSectionDividerNode() }),
        priority: 0,
      }),
    };
  }

  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const hr = document.createElement('hr');
    return { element: hr };
  }

  static importJSON(_serializedNode: SerializedSectionDividerNode): SectionDividerNode {
    return $createSectionDividerNode();
  }

  exportJSON(): SerializedSectionDividerNode {
    return {
      type: 'section-divider',
      version: 1,
    };
  }

  getTextContent(): string {
    return '---\n';
  }

  isInline(): false {
    return false;
  }

  decorate(): null {
    return null;
  }
}

export function $createSectionDividerNode(): SectionDividerNode {
  return new SectionDividerNode();
}

export function $isSectionDividerNode(
  node: LexicalNode | null | undefined,
): node is SectionDividerNode {
  return node instanceof SectionDividerNode;
}
